import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, IconButton, Popover } from '@mui/material';
import { Smile } from 'lucide-react';
import Lottie from 'lottie-react';

let _mapCache = null;
function useEmojiMap() {
  const [map, setMap] = useState(_mapCache);
  useEffect(() => {
    if (_mapCache) return;
    fetch('/lottie-emoji/_map.json')
      .then((r) => r.json())
      .then((d) => { _mapCache = d; setMap(d); });
  }, []);
  return map;
}

const GRID_COLS = 8;
const CELL = 32;

export default function SfEmojiPicker({ anchorEl, open, onClose, onSelect }) {
  const map = useEmojiMap();
  const [preview, setPreview] = useState(null);
  const [lottieData, setLottieData] = useState(null);
  const [search, setSearch] = useState('');

  const emojis = useMemo(() => (map ? Object.keys(map) : []), [map]);

  const filtered = useMemo(() => {
    if (!search) return emojis;
    const q = search.toLowerCase();
    return emojis.filter((e) => {
      const name = map[e]?.replace('.json', '').toLowerCase() || '';
      return name.includes(q);
    });
  }, [emojis, search, map]);

  const handleHover = useCallback(
    (emoji) => {
      if (!map) return;
      setPreview(emoji);
      setLottieData(null);
      fetch(`/lottie-emoji/${map[emoji]}`)
        .then((r) => r.json())
        .then(setLottieData)
        .catch(() => {});
    },
    [map]
  );

  const handleSelect = useCallback(
    (emoji) => {
      onSelect(emoji);
    },
    [onSelect]
  );

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setLottieData(null);
      setSearch('');
    }
  }, [open]);

  if (!map) return null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#1e1e24',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            overflow: 'hidden',
            width: GRID_COLS * CELL + 32 + (preview ? 120 : 0),
            display: 'flex',
          },
          onMouseDown: (e) => e.preventDefault(),
        },
      }}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      {/* Grid */}
      <Box sx={{ display: 'flex', flexDirection: 'column', width: GRID_COLS * CELL + 32, flexShrink: 0 }}>
        {/* Search */}
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
          <Box
            component="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              width: '100%',
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px',
              color: '#fafafa',
              fontSize: '0.75rem',
              px: 1,
              py: 0.5,
              outline: 'none',
              '&::placeholder': { color: 'rgba(161,161,170,0.5)' },
            }}
          />
        </Box>

        {/* Emoji grid */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            p: 1,
            maxHeight: 240,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
          }}
        >
          {filtered.map((emoji) => (
            <Box
              key={emoji}
              onMouseEnter={() => handleHover(emoji)}
              onClick={() => handleSelect(emoji)}
              sx={{
                width: CELL,
                height: CELL,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: '6px',
                fontSize: '1.15rem',
                lineHeight: 1,
                '&:hover': { bgcolor: 'rgba(139,92,246,0.15)' },
                transition: 'background 0.1s',
              }}
            >
              {emoji}
            </Box>
          ))}
          {filtered.length === 0 && (
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(161,161,170,0.5)', p: 1 }}>
              Nothing found
            </Typography>
          )}
        </Box>
      </Box>

      {/* Preview panel */}
      {preview && (
        <Box
          sx={{
            width: 120,
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1.5,
            gap: 1,
          }}
        >
          {lottieData ? (
            <Lottie
              key={preview}
              animationData={lottieData}
              loop={false}
              autoplay
              style={{ width: 72, height: 72 }}
            />
          ) : (
            <Box sx={{ width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>
              {preview}
            </Box>
          )}
          <Typography sx={{ fontSize: '0.6rem', color: 'rgba(161,161,170,0.6)', textAlign: 'center', wordBreak: 'break-word' }}>
            {map[preview]?.replace('.json', '')}
          </Typography>
        </Box>
      )}
    </Popover>
  );
}

export function EmojiTriggerButton({ onClick, buttonRef }) {
  return (
    <IconButton
      ref={buttonRef}
      size="small"
      className="nodrag nopan"
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onClick(e); }}
      sx={{
        p: 0.3,
        color: 'rgba(161,161,170,0.4)',
        '&:hover': { color: '#a78bfa', bgcolor: 'rgba(139,92,246,0.1)' },
      }}
    >
      <Smile size={13} />
    </IconButton>
  );
}
